import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const Partenaire = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    type: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:contact@aifrica.tech?subject=Partenariat - ${formData.organization}&body=Nom: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0AOrganisation: ${formData.organization}%0D%0AType: ${formData.type}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
    window.location.href = mailtoLink;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');

    :root {
      --ink: #0f2537;
      --ink-mid: #1a3a52;
      --ink-soft: #6b7280;
      --cream: #f8f9fa;
      --warm: #eef2f7;
      --terra: #1a3a52;
      --terra-lt: #f4c430;
      --gold: #f4c430;
      --sand: #f4c430;
      --white: #ffffff;
      --border: #e5e7eb;
      --font-d: 'Playfair Display', Georgia, serif;
      --font-ui: 'DM Sans', system-ui, sans-serif;
      --ease: cubic-bezier(0.16, 1, 0.3, 1);
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    html { scroll-behavior: smooth; }

    .partner-root {
      font-family: var(--font-ui);
      background: var(--cream);
      color: var(--ink);
      overflow-x: hidden;
    }

    /* ─── HERO SECTION ─── */
    .partner-hero {
      background: var(--ink);
      padding: 120px 52px 80px;
      position: relative;
      overflow: hidden;
    }

    .partner-hero::before {
      content: '';
      position: absolute;
      inset: 0;
      background-image: linear-gradient(135deg, rgba(244, 196, 48, 0.1) 0%, transparent 50%);
      pointer-events: none;
    }

    .partner-hero-inner {
      max-width: 1320px;
      margin: 0 auto;
      position: relative;
      z-index: 1;
    }

    .partner-badge {
      display: inline-flex;
      align-items: center;
      gap: 10px;
      font-size: 0.6rem;
      font-weight: 400;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: var(--terra-lt);
      margin-bottom: 28px;
    }

    .partner-badge::before {
      content: '';
      display: block;
      width: 24px; height: 2px;
      background: var(--terra-lt);
    }

    .partner-title {
      font-family: var(--font-ui);
      font-size: clamp(2rem, 3.5vw, 3rem);
      font-weight: 500;
      color: var(--white);
      line-height: 1.4;
      margin-bottom: 24px;
    }

    .partner-title em {
      font-style: italic;
      color: var(--terra-lt);
    }

    .partner-subtitle {
      font-size: 1.1rem;
      line-height: 1.7;
      color: rgba(255,255,255,0.7);
      max-width: 800px;
      margin-bottom: 40px;
    }

    /* ─── INTRODUCTION SECTION ─── */
    .partner-intro {
      padding: 96px 52px;
      background: var(--white);
      border-bottom: 1px solid var(--border);
    }

    .partner-intro-inner {
      max-width: 900px;
      margin: 0 auto;
      text-align: center;
    }

    .partner-intro-text {
      font-size: 1.1rem;
      line-height: 1.8;
      color: var(--ink-soft);
      margin-bottom: 32px;
    }

    .partner-intro-text strong {
      color: var(--terra-lt);
      font-weight: 600;
    }

    /* ─── PARTNER TYPES ─── */
    .partner-types {
      padding: 96px 52px;
      background: var(--warm);
    }

    .partner-types-inner {
      max-width: 1320px;
      margin: 0 auto;
    }

    .partner-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 32px;
      margin-top: 64px;
    }

    .partner-card {
      background: var(--white);
      border-radius: 12px;
      padding: 40px 32px;
      position: relative;
      transition: transform 0.3s var(--ease), box-shadow 0.3s var(--ease);
      border: 1px solid var(--border);
    }

    .partner-card:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(244, 196, 48, 0.15);
      border-color: var(--terra-lt);
    }

    .partner-icon {
      font-size: 2.5rem;
      margin-bottom: 24px;
      display: block;
    }

    .partner-card-title {
      font-family: var(--font-d);
      font-size: 1.5rem;
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 16px;
    }

    .partner-card-subtitle {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--terra-lt);
      margin-bottom: 20px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .partner-card-text {
      font-size: 0.95rem;
      line-height: 1.7;
      color: var(--ink-soft);
      margin-bottom: 24px;
    }

    .partner-benefits {
      background: var(--warm);
      border-radius: 8px;
      padding: 20px;
      margin-top: 20px;
    }

    .partner-benefits-title {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 12px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .partner-benefits-list {
      list-style: none;
    }

    .partner-benefits-list li {
      font-size: 0.9rem;
      color: var(--ink-soft);
      padding: 4px 0;
      position: relative;
      padding-left: 20px;
    }

    .partner-benefits-list li::before {
      content: '✓';
      position: absolute;
      left: 0;
      color: var(--terra-lt);
      font-weight: 600;
    }

    /* ─── CAPABILITIES SECTION ─── */
    .partner-capabilities {
      padding: 96px 52px;
      background: var(--ink);
      color: var(--white);
    }

    .partner-capabilities-inner {
      max-width: 900px;
      margin: 0 auto;
      text-align: center;
    }

    .capabilities-title {
      font-family: var(--font-d);
      font-size: 2.5rem;
      font-weight: 600;
      margin-bottom: 32px;
    }

    .capabilities-text {
      font-size: 1.1rem;
      line-height: 1.8;
      color: rgba(255,255,255,0.8);
    }

    /* ─── CTA SECTION ─── */
    .partner-cta {
      padding: 96px 52px;
      background: var(--warm);
    }

    .partner-cta-inner {
      max-width: 800px;
      margin: 0 auto;
      text-align: center;
    }

    .cta-title {
      font-family: var(--font-d);
      font-size: 2.5rem;
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 24px;
    }

    .cta-subtitle {
      font-size: 1.1rem;
      line-height: 1.7;
      color: var(--ink-soft);
      margin-bottom: 48px;
    }

    /* ─── CONTACT FORM ─── */
    .contact-form {
      background: var(--white);
      border-radius: 12px;
      padding: 48px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
      border: 1px solid var(--border);
    }

    .form-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      margin-bottom: 24px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
    }

    .form-group.full-width {
      grid-column: 1 / -1;
    }

    .form-label {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--ink);
      margin-bottom: 8px;
    }

    .form-input,
    .form-select,
    .form-textarea {
      padding: 12px 16px;
      border: 1px solid var(--border);
      border-radius: 6px;
      font-family: var(--font-ui);
      font-size: 0.95rem;
      transition: border-color 0.2s, box-shadow 0.2s;
      background: var(--white);
    }

    .form-input:focus,
    .form-select:focus,
    .form-textarea:focus {
      outline: none;
      border-color: var(--terra-lt);
      box-shadow: 0 0 0 3px rgba(244, 196, 48, 0.1);
    }

    .form-textarea {
      min-height: 120px;
      resize: vertical;
    }

    .form-submit {
      background: var(--terra);
      color: var(--white);
      border: none;
      padding: 16px 32px;
      border-radius: 6px;
      font-family: var(--font-ui);
      font-size: 0.95rem;
      font-weight: 600;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      cursor: pointer;
      transition: background 0.2s, transform 0.2s;
    }

    .form-submit:hover {
      background: var(--terra-lt);
      transform: translateY(-2px);
    }

    /* ─── RESPONSIVE ─── */
    @media (max-width: 960px) {
      .partner-hero { padding: 80px 24px 60px; }
      .partner-intro { padding: 64px 24px; }
      .partner-types { padding: 64px 24px; }
      .partner-capabilities { padding: 64px 24px; }
      .partner-cta { padding: 64px 24px; }
      .partner-grid { grid-template-columns: 1fr; gap: 24px; }
      .form-grid { grid-template-columns: 1fr; }
      .contact-form { padding: 32px 24px; }
    }

    @media (max-width: 640px) {
      .partner-title { font-size: 1.5rem; }
      .capabilities-title { font-size: 2rem; }
      .cta-title { font-size: 2rem; }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="partner-root">

        {/* ─── HERO SECTION ─── */}
        <section className="partner-hero">
          <div className="partner-hero-inner">
            <div className="partner-badge">Nos Partenaires</div>
            <h1 className="partner-title">
              L'Afrique ne peut pas attendre.<br />
              Ses talents <em>non plus</em>.
            </h1>
            <p className="partner-subtitle">
              Chez Aifrica, nous croyons profondément que c'est par la collaboration — entre le secteur privé, les institutions publiques, le monde académique et la société civile — que la Data et l'IA peuvent produire un impact réel et durable sur le continent africain.
            </p>
          </div>
        </section>

        {/* ─── INTRODUCTION SECTION ─── */}
        <section className="partner-intro">
          <div className="partner-intro-inner">
            <p className="partner-intro-text">
              C'est pourquoi nous tissons des partenariats <strong>solides, complémentaires et engagés</strong> autour d'une vision commune : mettre la technologie au service du développement africain.
            </p>
            <p className="partner-intro-text">
              Nous construisons un écosystème de co-créateurs, convaincus que la Data et l'IA peuvent changer les règles du jeu pour les entreprises africaines — <strong>maintenant</strong>.
            </p>
            <p className="partner-intro-text">
              Rejoindre l'écosystème Aifrica, c'est prendre une place fondatrice dans un mouvement qui s'écrit aujourd'hui.
            </p>
          </div>
        </section>

        {/* ─── PARTNER TYPES ─── */}
        <section className="partner-types">
          <div className="partner-types-inner">
            <div className="partner-badge" style={{ marginBottom: '48px', textAlign: 'center' }}>
              Devenez Partenaire
            </div>
            
            <div className="partner-grid">
              {/* Experts & Consultants */}
              <div className="partner-card">
                <div className="partner-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f4c430" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2v8m0 4v8m0-14a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>
                    <path d="M12 14a5 5 0 0 0-5 5v3h10v-3a5 5 0 0 0-5-5z"/>
                  </svg>
                </div>
                <h3 className="partner-card-title">Experts & Consultants</h3>
                <div className="partner-card-subtitle">Bienvenue dans le club.</div>
                <p className="partner-card-text">
                  Vous avez l'expertise. Nous avons les projets les plus stratégiques du continent. Rejoindre le réseau Aifrica, c'est intervenir là où ça compte — et bénéficier d'un accès à des infrastructures tech de pointe (LLMs, GPU, datasets africains) pour démultiplier votre impact bien au-delà de ce que vous pourriez accomplir seul.
                </p>
                <div className="partner-benefits">
                  <div className="partner-benefits-title">Ce que vous y gagnez :</div>
                  <ul className="partner-benefits-list">
                    <li>Visibilité accrue</li>
                    <li>Projets à haute valeur ajoutée</li>
                    <li>Communauté d'excellence</li>
                  </ul>
                </div>
              </div>

              {/* Partenaires Académiques */}
              <div className="partner-card">
                <div className="partner-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f4c430" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                    <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                  </svg>
                </div>
                <h3 className="partner-card-title">Partenaires Académiques</h3>
                <div className="partner-card-subtitle">Réduisons le fossé entre la théorie et le terrain.</div>
                <p className="partner-card-text">
                  Nous offrons à vos étudiants et chercheurs un accès à des cas d'usage réels, des données concrètes et des problématiques business directement issus du tissu économique africain. En retour, vous bénéficiez d'une passerelle directe entre vos laboratoires et le monde de l'entreprise — pour préparer vos talents à l'économie de demain, pas à celle d'hier.
                </p>
                <div className="partner-benefits">
                  <div className="partner-benefits-title">Ce que vous y gagnez :</div>
                  <ul className="partner-benefits-list">
                    <li>Visibilité terrain</li>
                    <li>Insertion professionnelle accélérée</li>
                    <li>Recherche appliquée à fort impact</li>
                  </ul>
                </div>
              </div>

              {/* Partenaires Institutionnels & Gouvernementaux */}
              <div className="partner-card">
                <div className="partner-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f4c430" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 21h18M3 10h18M3 7l9-4 9 4M8 21v-11M16 21v-11M12 21v-11"/>
                  </svg>
                </div>
                <h3 className="partner-card-title">Partenaires Institutionnels & Gouvernementaux</h3>
                <div className="partner-card-subtitle">Ensemble, donnons une colonne vertébrale data aux politiques publiques.</div>
                <p className="partner-card-text">
                  Nous accompagnons les décideurs publics qui veulent que leurs stratégies digitales produisent des résultats mesurables — pas seulement des rapports. Aifrica vous offre une expertise terrain pour traduire vos ambitions numériques en actions concrètes, adaptées aux réalités locales.
                </p>
                <div className="partner-benefits">
                  <div className="partner-benefits-title">Ce que vous y gagnez :</div>
                  <ul className="partner-benefits-list">
                    <li>Capacité d'exécution opérationnelle</li>
                    <li>Lecture fine des usages locaux</li>
                    <li>Partenaire aligné sur vos objectifs de souveraineté numérique</li>
                  </ul>
                </div>
              </div>

              {/* ONG & Organisations Internationales */}
              <div className="partner-card">
                <div className="partner-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#f4c430" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16.5 12.5a3 3 0 0 0 0 6l3 0a3 3 0 0 0 0-6l-3 0M7.5 12.5a3 3 0 0 1 0 6l-3 0a3 3 0 0 1 0-6l3 0M12 12.5v6M12 12.5a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3v0a3 3 0 0 1-3-3v0a3 3 0 0 1 3-3z"/>
                  </svg>
                </div>
                <h3 className="partner-card-title">ONG & Organisations Internationales</h3>
                <div className="partner-card-subtitle">La donnée peut sauver des vies — encore faut-il savoir la lire.</div>
                <p className="partner-card-text">
                  Vous portez des missions essentielles dans la santé, l'éducation, l'agriculture ou la finance inclusive. Nous mettons la puissance de la Data et de l'IA au service de ces missions — pour mesurer l'impact réel de vos actions, optimiser vos ressources et toucher les populations qui en ont le plus besoin.
                </p>
                <div className="partner-benefits">
                  <div className="partner-benefits-title">Ce que vous y gagnez :</div>
                  <ul className="partner-benefits-list">
                    <li>Outils d'analyse adaptés à vos contraintes</li>
                    <li>Meilleure lisibilité de votre impact pour vos donateurs et parties prenantes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Partenaire;
